// Pure helpers for the Work Plan page (day roster planner). Framework-free so vitest can
// cover the timeline math and label logic without a DOM.
import { DateTime } from "luxon";
import { hashHue } from "@/components/pages/calendar/slots-heatmap/heatmap-utils";
import { minutesFromHHMM, sessionLabelFor } from "@/lib/work-plan-sessions";
import {
  idsSortedByName,
  moveIdByDelta,
  moveIdToIndex,
  orderByIds,
} from "@/lib/entity-order";

// Re-exported: the planner's existing callers import it from here.
export { minutesFromHHMM };

// The FALLBACK timeline lane: 09:00–18:00 (the density redesign's 540-minute day) with the
// AM/PM drop boundary at 13:30. Session hours are CONFIGURABLE (Settings → Work plan), so the
// page derives its real lane from the day bundle's `sessions` via timelineLane(); these
// constants only stand in when the sessions are missing or malformed — and they keep the
// lane-less call forms (`timelinePct(min)`, `timelineSpan(a, b)`) meaning what they always did.
export const TIMELINE_START_MIN = 9 * 60;
export const TIMELINE_SPAN_MIN = 9 * 60;
// Drops on the timeline left of 13:30 assign AM, right of it PM (the concept's midpoint
// between the AM end and the PM body).
export const TIMELINE_AMPM_DROP_BOUNDARY_MIN = 13.5 * 60;

function hourRange(startMin, endMin) {
  const first = Math.floor(startMin / 60);
  const last = Math.ceil(endMin / 60);
  return Array.from({ length: Math.max(0, last - first + 1) }, (_, index) => first + index);
}

export const DEFAULT_TIMELINE_LANE = Object.freeze({
  startMin: TIMELINE_START_MIN,
  spanMin: TIMELINE_SPAN_MIN,
  boundaryMin: TIMELINE_AMPM_DROP_BOUNDARY_MIN,
  hours: Object.freeze(hourRange(TIMELINE_START_MIN, TIMELINE_START_MIN + TIMELINE_SPAN_MIN)),
  firstKey: "am",
  secondKey: "pm",
});

/**
 * The timeline lane for a day's configured sessions: from the floor-hour of the earliest
 * start to the ceil-hour of the latest end (so 08:30–17:30 sessions draw on an 08:00–18:00
 * lane instead of clipping), with the AM/PM drop boundary at the midpoint of the gap between
 * the first session's end and the second's start. `hours` are the axis labels / gridlines.
 * Anything unusable (no sessions, bad times) falls back to DEFAULT_TIMELINE_LANE.
 */
export function timelineLane(sessions) {
  const defs = (Array.isArray(sessions) ? sessions : [])
    .map((session) => ({
      key: String(session?.key || "").trim(),
      start: minutesFromHHMM(session?.start),
      end: minutesFromHHMM(session?.end),
    }))
    .filter((session) => session.key && session.start >= 0 && session.end > session.start)
    .sort((a, b) => a.start - b.start);
  if (defs.length === 0) return DEFAULT_TIMELINE_LANE;
  const startMin = Math.floor(defs[0].start / 60) * 60;
  const endMin = Math.ceil(Math.max(...defs.map((session) => session.end)) / 60) * 60;
  if (endMin <= startMin) return DEFAULT_TIMELINE_LANE;
  const first = defs[0];
  const second = defs[1] || null;
  // A single session owns the whole lane; the boundary sits past its end so every drop is "first".
  const boundaryMin = second ? Math.round((first.end + second.start) / 2) : endMin;
  return {
    startMin,
    spanMin: endMin - startMin,
    boundaryMin,
    hours: hourRange(startMin, endMin),
    firstKey: first.key,
    secondKey: second ? second.key : first.key,
  };
}

/** Percentage offset of a clock time inside the timeline lane, clamped to [0, 100]. */
export function timelinePct(minutes, lane = DEFAULT_TIMELINE_LANE) {
  const pct = ((minutes - lane.startMin) / lane.spanMin) * 100;
  return Math.min(100, Math.max(0, pct));
}

/** Left/width percentages for a [start, end] "HH:MM" pair on the timeline. */
export function timelineSpan(start, end, lane = DEFAULT_TIMELINE_LANE) {
  const startMin = minutesFromHHMM(start);
  const endMin = minutesFromHHMM(end);
  if (startMin < 0 || endMin <= startMin) return null;
  const left = timelinePct(startMin, lane);
  return { left, width: Math.max(1.5, timelinePct(endMin, lane) - left) };
}

/** Maps a drop x-fraction (0..1) across the lane onto the first ("am") or second ("pm") session key. */
export function sessionForLaneFraction(fraction, lane = DEFAULT_TIMELINE_LANE) {
  const minutes = lane.startMin + fraction * lane.spanMin;
  return minutes < lane.boundaryMin ? lane.firstKey : lane.secondKey;
}

/** "08:00–18:00" — the lane's extent for titles and legends. */
export function formatLaneRange(lane = DEFAULT_TIMELINE_LANE) {
  const hhmm = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  return `${hhmm(lane.startMin)}–${hhmm(lane.startMin + lane.spanMin)}`;
}

// The role vocabulary is CONFIGURABLE (Settings → Work roles; served in the day bundle as
// workRoles and by GET /api/work-roles) — nothing here hardcodes role ids or labels.
//
// The FIRST configured role is the default bucket: most support staff carry no explicit
// workRole, and labelling them with an invented word ("Support") that appears nowhere in
// the vocabulary reads as stale. They show the first role's label AND match its filter
// pill, so the label and the filter never disagree.

/** Fallback label when an org has retired every role — nothing else to call anyone. */
const NO_ROLES_LABEL = "Support";

/**
 * Resolves the vocabulary into everything the planner needs: id → label lookup, plus the
 * default (first) role that unassigned staff belong to.
 */
export function buildRoleView(workRoles) {
  const list = (workRoles || []).filter((role) => role?.id);
  const byId = {};
  for (const role of list) {
    byId[role.id] = role.label || role.id;
  }
  const first = list[0] || null;
  return {
    byId,
    defaultRoleId: first?.id || "",
    defaultLabel: first ? first.label || first.id : NO_ROLES_LABEL,
  };
}

/** The rail's filter pills: All + one pill per configured role. */
export function rolePillsFrom(workRoles) {
  return [
    { id: "all", label: "All" },
    ...(workRoles || [])
      .filter((role) => role?.id)
      .map((role) => ({ id: role.id, label: role.label || role.id })),
  ];
}

/**
 * Display label for a person's role. Doctors are always "Doctor"; no role at all reads as
 * the default (first) role; a workRole id no longer in the vocabulary shows the raw id, so
 * a retired role stays visible rather than silently reading as the default.
 */
export function workRoleLabel(staff, roleView) {
  if (!staff) return "";
  if (staff.isDoctor || staff.userType === "doctor") return "Doctor";
  const roleId = staff.workRole || "";
  if (!roleId) return roleView?.defaultLabel || NO_ROLES_LABEL;
  return roleView?.byId?.[roleId] || roleId;
}

/**
 * Whether a person belongs under a role filter pill. Unassigned staff fall under the
 * default (first) role — the same rule workRoleLabel uses, so what a row says and where it
 * filters always agree.
 */
export function matchesRolePill(staff, pillId, roleView) {
  if (pillId === "all") return true;
  const roleId = staff?.workRole || "";
  if (roleId) return roleId === pillId;
  return pillId === (roleView?.defaultRoleId || "");
}

export function initialsFromName(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/**
 * Deterministic identity tone for a person or clinic (the calendar convention: hashHue →
 * oklch). `solid` for swatches, `bg`/`fg` for tinted avatars and timeline bars.
 */
export function identityTone(id) {
  const hue = hashHue(String(id || ""));
  return {
    solid: `oklch(0.62 0.16 ${hue})`,
    bg: `oklch(0.94 0.05 ${hue})`,
    fg: `oklch(0.42 0.12 ${hue})`,
  };
}

// ---- civil date helpers (all "YYYY-MM-DD") ----

export function shiftDate(dateKey, days) {
  const dt = DateTime.fromISO(dateKey);
  return dt.isValid ? dt.plus({ days }).toISODate() : dateKey;
}

/** "Tue 4 Aug 2026" — the header breadcrumb. */
export function formatDayFull(dateKey) {
  const dt = DateTime.fromISO(dateKey);
  return dt.isValid ? dt.toFormat("EEE d LLL yyyy") : dateKey;
}

/** "Tue 4 Aug" — toasts and the copy popover. */
export function formatDayLabel(dateKey) {
  const dt = DateTime.fromISO(dateKey);
  return dt.isValid ? dt.toFormat("EEE d LLL") : dateKey;
}

/** "4 Aug" + "Tue" for the date pill. */
export function formatDayShort(dateKey) {
  const dt = DateTime.fromISO(dateKey);
  return dt.isValid ? dt.toFormat("d LLL") : dateKey;
}

export function weekdayShort(dateKey) {
  const dt = DateTime.fromISO(dateKey);
  return dt.isValid ? dt.toFormat("EEE") : "";
}

/**
 * The Day Table's week strip: Mon–Sat of the week containing `dateKey`, each as
 * { date, label } with the tile label the design uses ("MON 3"). Sunday is deliberately
 * absent — the roster is a Mon–Sat operation — but a Sunday `dateKey` still resolves to
 * its own (preceding-Monday) week rather than crashing the strip.
 */
export function weekStripDates(dateKey) {
  const dt = DateTime.fromISO(dateKey);
  if (!dt.isValid) return [];
  const monday = dt.minus({ days: dt.weekday - 1 });
  return Array.from({ length: 6 }, (_, index) => {
    const day = monday.plus({ days: index });
    return { date: day.toISODate(), label: `${day.toFormat("EEE").toUpperCase()} ${day.day}` };
  });
}

/** The posting's effective window: its override, else the session default. */
export function postingTimes(posting, sessionsByKey) {
  const def = sessionsByKey?.[posting?.session] || null;
  const hasOverride = Boolean(posting?.startTime && posting?.endTime);
  return {
    start: hasOverride ? posting.startTime : def?.start || "",
    end: hasOverride ? posting.endTime : def?.end || "",
    custom:
      hasOverride && (posting.startTime !== def?.start || posting.endTime !== def?.end),
  };
}

/**
 * Who is where today: personId → { [sessionKey]: [tenantId, …] }. Support postings and
 * read-only doctor sessions land in the SAME map, so a chip's day flag reads identically
 * whether the person is rostered here or in Sessions.
 */
export function buildDayCoverage(postings, doctorSessions) {
  const coverage = {};
  const add = (personId, session, tenantId) => {
    if (!personId || !session || !tenantId) return;
    const bySession = (coverage[personId] ||= {});
    const tenantIds = (bySession[session] ||= []);
    if (!tenantIds.includes(tenantId)) tenantIds.push(tenantId);
  };
  for (const posting of postings || []) add(posting.staffId, posting.session, posting.tenantId);
  for (const item of doctorSessions || []) add(item.doctorId, item.session, item.tenantId);
  return coverage;
}

function sessionLabel(session) {
  return session?.label || String(session?.key || "").toUpperCase();
}

/**
 * 3-letter code for the Day Table's move markers ("→ CLE"). Derived from the display name
 * rather than shortName — shortName is not unique in real data (CAMDEN and L12 are both
 * "Orchard"), and the marker's tooltip carries the full names anyway.
 */
export function clinicCode(name) {
  return String(name || "")
    .replace(/[^\p{L}\p{N}]/gu, "")
    .slice(0, 3)
    .toUpperCase();
}

/**
 * Name a clinic the way the planner's ROWS name it — `name` first. shortName is not
 * unique in real data (CAMDEN and L12 are both "Orchard"), so a pill built from it points
 * at two different rows at once; `name` is also what the row header shows, so the eye can
 * follow "PM CAMDEN" straight down the grid.
 */
function clinicLabel(clinicsById, tenantId) {
  const clinic = clinicsById?.[tenantId];
  return clinic?.name || clinic?.shortName || "";
}

/**
 * The day flag for one assignment chip in a CLINIC row: null when the person is at this
 * clinic for every session of the day (nothing worth flagging), otherwise the person's
 * WHOLE day in two parts — the session they are here for (`session`, rendered large) and
 * what happens in the other half (`detail`): the location they move to ("PM Clementi", the
 * case planners keep getting wrong) or nothing at all ("only").
 *
 * `back` is the arrow direction: the other half runs EARLIER in the day, so a PM chip
 * points back at the AM it came from while an AM chip points forward at the PM to come.
 */
export function dayCoverageFlag({ coverage, personId, sessionKey, tenantId, sessions, clinicsById }) {
  const list = sessions || [];
  const hereIndex = list.findIndex((session) => session.key === sessionKey);
  if (hereIndex < 0 || list.length < 2) return null;
  const here = list[hereIndex];

  const bySession = coverage?.[personId] || {};
  const elsewhere = [];
  const free = [];
  list.forEach((session, index) => {
    if (index === hereIndex) return;
    const tenantIds = bySession[session.key] || [];
    if (tenantIds.includes(tenantId)) return; // still here that session — nothing to say
    const away = tenantIds.filter((id) => id !== tenantId);
    if (away.length > 0) elsewhere.push({ session, index, tenantIds: away });
    else free.push(session);
  });
  if (elsewhere.length === 0 && free.length === 0) return null;

  const hereLabel = sessionLabel(here);
  if (elsewhere.length > 0) {
    const parts = elsewhere.map(({ session, tenantIds }) => {
      const names = tenantIds.map((id) => clinicLabel(clinicsById, id)).filter(Boolean);
      return `${sessionLabel(session)} ${names.join(" / ") || "elsewhere"}`;
    });
    // The Day Table renders moves as "→ ᴾᴹ CLE": the other half's session in small caps
    // plus a 3-letter code. Falls back to "away" for an unnamed clinic.
    const shortParts = elsewhere.map(({ session, tenantIds }) => ({
      session: sessionLabel(session),
      code:
        tenantIds
          .map((id) => clinicCode(clinicLabel(clinicsById, id)))
          .filter(Boolean)
          .join(" / ") || "away",
    }));
    return {
      moves: true,
      session: hereLabel,
      detail: parts.join(" · "),
      shortDetail: shortParts.map((part) => part.code).join(" · "),
      shortParts,
      back: elsewhere[0].index < hereIndex,
      title: `Covers another location today — ${parts.join(", ")}. Here for the ${hereLabel} session only.`,
    };
  }
  return {
    moves: false,
    session: hereLabel,
    detail: "only",
    shortDetail: "only",
    back: false,
    title: `Here for the ${hereLabel} session only — not rostered for the ${free
      .map(sessionLabel)
      .join(" or ")} session.`,
  };
}

/**
 * The day's booked load from the bundle's sparse appointmentCounts: per clinic session
 * (`byCell`), per session across every clinic (`bySession`) and the day total. Absent
 * entries mean zero — the server omits empty sessions.
 *
 * `supported` separates "nothing booked" from "this server doesn't send counts": an older
 * backend omits the field entirely, and rendering a confident "0 appts" on every cell
 * would be a lie rather than a missing feature.
 */
export function buildAppointmentLoad(appointmentCounts) {
  const byCell = {};
  const bySession = {};
  const supported = Array.isArray(appointmentCounts);
  let total = 0;
  for (const item of appointmentCounts || []) {
    const count = Number(item?.count) || 0;
    if (!item?.tenantId || !item?.session || count <= 0) continue;
    byCell[`${item.tenantId}|${item.session}`] = (byCell[`${item.tenantId}|${item.session}`] || 0) + count;
    bySession[item.session] = (bySession[item.session] || 0) + count;
    total += count;
  }
  return { byCell, bySession, total, supported };
}

// ---- clinic visibility (device-local, preference workPlanHiddenClinicIds) ----

/**
 * Splits the day's clinics into what the planner shows and what the user hid. The stored
 * preference is the HIDDEN set so a clinic opened later is visible by default; ids no longer
 * in the day are simply inert, which is why `hiddenCount` counts hidden clinics that still
 * exist rather than the raw list length.
 */
export function clinicVisibility(clinics, hiddenClinicIds) {
  const hidden = new Set((hiddenClinicIds || []).map((id) => String(id || "").trim()).filter(Boolean));
  const all = (clinics || []).filter((clinic) => clinic?.id);
  const visible = all.filter((clinic) => !hidden.has(clinic.id));
  return { visible, hidden, total: all.length, hiddenCount: all.length - visible.length };
}

// Clinic ordering is the generic entity-order helper set, re-exported under the
// names this module has always used so every Work Plan call site (and its tests)
// keep working. See src/lib/entity-order.js for the ordering semantics.
export const orderClinics = orderByIds;
export const moveClinicInOrder = moveIdByDelta;
export const moveClinicToIndex = moveIdToIndex;
export const clinicIdsSortedByName = idsSortedByName;

/** Next hide-list after flipping one clinic between shown and hidden. */
export function toggleClinicHidden(hiddenClinicIds, clinicId) {
  const current = (hiddenClinicIds || []).map((id) => String(id || "").trim()).filter(Boolean);
  const id = String(clinicId || "").trim();
  if (!id) return current;
  return current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
}

/**
 * "AM Bedok · PM Clementi" for the rail's doctor rows — ordered and labelled by the day's
 * configured `sessions` (a session key the config doesn't know sorts last and reads as its
 * upper-cased key, which is also the whole behaviour when no sessions are passed).
 */
export function doctorWhereabouts(doctorSessions, clinicsById, sessions) {
  const order = (Array.isArray(sessions) ? sessions : []).map((session) => String(session?.key || ""));
  const rank = (key) => {
    const index = order.indexOf(key);
    if (index >= 0) return index;
    // Fallback ranking keeps today's AM-first reading without any config.
    return order.length + (key === "am" ? 0 : key === "pm" ? 1 : 2);
  };
  const parts = [];
  const seen = new Set();
  const ordered = [...(doctorSessions || [])].sort((a, b) => rank(a.session) - rank(b.session));
  for (const item of ordered) {
    if (seen.has(item.session)) continue;
    seen.add(item.session);
    const clinic = clinicsById?.[item.tenantId];
    const short = clinic?.shortName || clinic?.name || "";
    if (!short) continue;
    parts.push(`${sessionLabelFor(sessions, item.session)} ${short}`);
  }
  return parts.join(" · ");
}

/** The result-toast line for a copy, mirroring the concept's phrasing. */
export function copyResultToast(result, fromDate, weekly) {
  const copied = Number(result?.copied) || 0;
  const replaced = Number(result?.replaced) || 0;
  const skippedMc = Number(result?.skippedMc) || 0;
  let line = `${copied} support posting${copied === 1 ? "" : "s"} copied from ${formatDayLabel(fromDate)}`;
  if (weekly) line += " (whole week)";
  if (replaced > 0) line += ` · ${replaced} replaced`;
  if (skippedMc > 0) line += ` · ${skippedMc} skipped (confirmed MC)`;
  return `${line}. Doctor sessions untouched.`;
}
